// App.js
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import { useEffect } from "react";
import "react-toastify/dist/ReactToastify.css";
import { ToastContainer } from "react-toastify";
import { ThemeProvider } from "./context/ThemeContext";
import { useAuth } from "./context/AuthProvider";
import Layout from "./components/Layout";
import Home from "./pages/Home";
import Internship from "./pages/Internship";
import Registration from "./pages/Registration";
import Tasks from "./pages/Tasks";
import Dashboard from "./pages/Dashboard";
import Login from "./pages/Login";
import Courses from "./pages/Courses";
import Skills from "./pages/Skills";
import Lessons from "./pages/Lessons";
import Tutors from "./pages/Tutors";
import Classes from "./pages/Classes";
import PrivateRoute from "./components/PrivateRoute";
import PublicRoute from "./components/PublicRoute";
import Signup from "./pages/Signup";
import Students from "./pages/Students";
import Batches from "./pages/Batches";
import Attendance from "./pages/Attendance";
import Leaves from "./pages/Leaves";
import Projects from "./pages/Projects";
import Profile from "./pages/Profile";
import Salary from "./pages/Salary";
import Colleges from "./pages/Colleges";
import Revenue from "./pages/Revenue";
import FeeManagement from "./pages/FeeManagement";
import Holidays from "./pages/Holidays";
import Materials from "./pages/Materials";
import { 
  refreshNotificationToken,
  registerNotificationServiceWorker,
  setupForegroundNotifications,
  isNotificationSupported 
} from "./services/pushNotificationService";
// Import cleanup scheduler to auto-initialize
import "./services/cleanupScheduler";

function AppContent() {
  const { user } = useAuth();

  useEffect(() => {
    if (!user || !isNotificationSupported()) {
      return undefined;
    }

    const userId = user.id || user.uid;
    if (!userId) return undefined;

    const unsubscribe = setupForegroundNotifications();
    registerNotificationServiceWorker();

    // Refresh an existing grant silently. A new permission request is only made
    // from the notification bell so it follows a user gesture.
    if (Notification.permission === "granted") {
      refreshNotificationToken(userId).catch((error) => {
        console.warn("Unable to refresh the notification token:", error);
      });
    }

    return unsubscribe;
  }, [user]);

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<Home />} />
      <Route path="/internship" element={<Internship />} />
      <Route path="/register" element={<PublicRoute><Registration /></PublicRoute>} />
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/signup" element={<PublicRoute><Signup /></PublicRoute>} />

      {/* Protected routes - Dashboard */}
      <Route
            path="/"
            element={
              <PrivateRoute>
                <Layout />
              </PrivateRoute>
            }
          >
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="tasks" element={<Tasks />} />
            <Route
              path="projects"
              element={
                <PrivateRoute roles={["admin", "tutor"]}>
                  <Projects />
                </PrivateRoute>
              }
            />
            <Route
              path="skills"
              element={
                <PrivateRoute roles={["admin", "tutor"]}>
                  <Skills />
                </PrivateRoute>
              }
            />
            <Route
              path="courses"
              element={
                <PrivateRoute roles={["admin", "tutor"]}>
                  <Courses />
                </PrivateRoute>
              }
            />
            <Route
              path="lessons"
              element={
                <PrivateRoute roles={["admin", "tutor"]}>
                  <Lessons />
                </PrivateRoute>
              }
            />
            <Route
              path="tutors"
              element={
                <PrivateRoute roles={["admin"]}>
                  <Tutors />
                </PrivateRoute>
              }
            />
            <Route
              path="classes"
              element={
                <PrivateRoute roles={["admin", "tutor"]}>
                  <Classes />
                </PrivateRoute>
              }
            />
            <Route
              path="materials"
              element={
                <PrivateRoute roles={["admin", "tutor"]}>
                  <Materials />
                </PrivateRoute>
              }
            />
            <Route
              path="students"
              element={
                <PrivateRoute roles={["admin", "tutor"]}>
                  <Students />
                </PrivateRoute>
              }
            />
            <Route
              path="batches"
              element={
                <PrivateRoute roles={["admin", "tutor"]}>
                  <Batches />
                </PrivateRoute>
              }
            />
            <Route
              path="attendance"
              element={
                <PrivateRoute roles={["admin", "tutor"]}>
                  <Attendance />
                </PrivateRoute>
              }
            />
            <Route
              path="leaves"
              element={
                <PrivateRoute>
                  <Leaves />
                </PrivateRoute>
              }
            />
            <Route
              path="colleges"
              element={
                <PrivateRoute roles={["admin", "tutor"]}>
                  <Colleges />
                </PrivateRoute>
              }
            />
            <Route
              path="revenue"
              element={
                <PrivateRoute roles={["admin"]}>
                  <Revenue />
                </PrivateRoute>
              }
            />
            <Route
              path="salary"
              element={
                <PrivateRoute roles={["admin"]}>
                  <Salary />
                </PrivateRoute>
              }
            />
            <Route
              path="fees"
              element={
                <PrivateRoute roles={["admin"]}>
                  <FeeManagement />
                </PrivateRoute>
              }
            />
            <Route
              path="profile"
              element={
                <PrivateRoute>
                  <Profile />
                </PrivateRoute>
              }
            />
            <Route
              path="holidays"
              element={
                <PrivateRoute>
                  <Holidays />
                </PrivateRoute>
              }
            />
          </Route>
        </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <Router>
        <AppContent />
        <ToastContainer position="top-right" autoClose={3000} />
      </Router>
    </ThemeProvider>
  );
}

export default App;
